import React, { useState } from 'react';
import InvitationDesignerV2 from '../_components/InvitationDesigner/InvitationDesignerV2';
import DesignTestPanel from '../_components/DesignTestPanel/DesignTestPanel';
import { Event, InvitationType } from '../_graphql/generated/operations';
import { InvitationDesign } from '../_types/invitation-design-v2';

// Mock test data
const mockEvent: Event = {
  id: 'test-event-1',
  eventName: 'Sarah & Michael\'s Wedding',
  eventDateTime: new Date('2025-06-15T16:00:00').getTime(),
  createdAt: Date.now(),
  updatedAt: Date.now(),
  invitations: [],
  invitationTypes: []
};

const mockInvitationTypes: InvitationType[] = [
  {
    id: 'type-1',
    eventId: 'test-event-1',
    name: 'Save the Date',
    description: 'Early notification for guests to save the wedding date',
    designCustomizations: null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    event: mockEvent
  },
  {
    id: 'type-2',
    eventId: 'test-event-1',
    name: 'Full Invitation',
    description: 'Complete invitation with all event details',
    designCustomizations: null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    event: mockEvent
  },
  {
    id: 'type-3',
    eventId: 'test-event-1',
    name: 'Ceremony Only',
    description: 'Invitation for ceremony attendance only',
    designCustomizations: null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    event: mockEvent
  }
];

// Add invitation types to the event
mockEvent.invitationTypes = mockInvitationTypes;

const InvitationDesignerStandaloneV2: React.FC = () => {
  const [savedDesigns, setSavedDesigns] = useState<Map<string, InvitationDesign>>(new Map());
  const [currentDesign, setCurrentDesign] = useState<InvitationDesign | null>(null);
  const [currentInvitationType, setCurrentInvitationType] = useState<InvitationType | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showTestPanel, setShowTestPanel] = useState(true);
  const [designerKey, setDesignerKey] = useState(0); // Force re-render of designer

  const handleSave = (design: InvitationDesign, invitationType?: InvitationType) => {
    if (invitationType) {
      // Store the design
      const newDesigns = new Map(savedDesigns);
      newDesigns.set(invitationType.id, design);
      setSavedDesigns(newDesigns);
      
      // Store current invitation type
      setCurrentInvitationType(invitationType);
      
      // Update current design
      setCurrentDesign(design);
      
      // Show success message
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  const handleCancel = () => {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-lg font-semibold">Invitation Designer V2 Test</h1>
            <div className="flex gap-4">
              <a 
                href="/designer" 
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Old Designer (V1)
              </a>
              <a 
                href="/" 
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Back to Dashboard
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Success notification */}
      {showSuccess && (
        <div className="fixed top-20 right-4 bg-green-500 text-white px-6 py-3 rounded-md shadow-lg z-50 animate-fade-in">
          Design saved successfully!
        </div>
      )}

      {/* Designer component */}
      <div className="h-[calc(100vh-4rem)]">
        <InvitationDesignerV2
          key={designerKey} // Force re-render when key changes
          event={mockEvent}
          onSave={handleSave}
          onCancel={handleCancel}
          // Pass the latest design for the current type
          initialDesign={currentInvitationType ? savedDesigns.get(currentInvitationType.id) : undefined}
        />
      </div>


      {/* Design Test Panel */}
      <DesignTestPanel
        designV2={currentDesign}
        isOpen={showTestPanel}
        onClose={() => setShowTestPanel(false)}
        onApplyDesign={(updatedDesign) => {
          setCurrentDesign(updatedDesign);
          
          // Update the saved designs
          if (currentInvitationType) {
            const newDesigns = new Map(savedDesigns);
            newDesigns.set(currentInvitationType.id, updatedDesign);
            setSavedDesigns(newDesigns);
          }
          
          // Force the designer to re-render with new design
          setDesignerKey(prev => prev + 1);
          
          // Show success message
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 3000);
        }}
      />
    </div>
  );
};

export default InvitationDesignerStandaloneV2;